import { api, LightningElement, wire } from "lwc";

import { EffectAdapter } from "common/effectAdapter";
import { LabelsAdapter, LabelsAdapterValue } from "common/labelsAdapter";
import { assignedSlotNames } from "common/slot";

import * as locales from "./locales";

import type {
  Config,
  State,
  EmptyState,
  ErrorState,
  LoadingState,
} from "./types";
export type { Config, State, EmptyState, ErrorState, LoadingState };

type Nullable<T> = T | null;

export default class extends LightningElement {
  @api config: Config = null!;
  @api variant?: string;

  private state: State = "loaded";

  @wire(LabelsAdapter, { locales })
  private labels: LabelsAdapterValue = null!;

  @wire(EffectAdapter, { config: "$config" })
  private onConfigChange({ config }: { config: Config }) {
    // TODO: Debounce state change to avoid flickering a loading state
    this.state = config.state;
    this.template.host.setAttribute("data-state", this.state);
  }

  private get empty(): Nullable<EmptyState> {
    if (this.state !== "empty") return null;
    return {
      description: this.labels["empty.description"],
      size: "small",
      layout: "standard",
      imgVariant: "humming-bird",
      title: this.labels["empty.title"],
      ...this.config.empty,
    };
  }

  private get emptyFallback() {
    return !this.slots.includes("empty");
  }

  private get error(): Nullable<ErrorState> {
    if (this.state !== "error") return null;
    return {
      description: this.labels["error.description"],
      iconSprite: "utility",
      iconSymbol: "trail",
      refresh: true,
      refreshLabel: this.labels["error.refresh"],
      title: this.labels["error.title"],
      ...this.config.error,
    };
  }

  private get errorFallback() {
    return !this.slots.includes("error");
  }

  private get isLoaded() {
    return this.state === "loaded";
  }

  private get loading(): Nullable<LoadingState> {
    if (this.state !== "loading") return null;
    return {
      stencilCount: 3,
      stencilVariant: "brick",
      ...this.config.loading,
    };
  }

  private get loadingFallback() {
    return !this.slots.includes("loading");
  }

  private slots: string[] = [];

  private onRefresh() {
    this.setFocusOnRefresh();
    this.dispatchEvent(new CustomEvent("refresh"));
  }

  private setFocusOnRefresh() {
    const refreshButton = this.template.querySelector(".state__refresh");

    if (refreshButton) {
      refreshButton.focus();
    } else {
      const logoAnchor = document.querySelector(
        "[data-tracking-type=logo]"
      ) as HTMLElement;

      logoAnchor?.focus();
    }
  }

  private onSlotChange() {
    this.slots = assignedSlotNames(this.template);
  }
}

export function stateFromLearningCollection(
  items: unknown[] | null,
  error: unknown | null
): State {
  return error
    ? "error"
    : Array.isArray(items)
    ? items.length === 0
      ? "empty"
      : "loaded"
    : "loading";
}
